import React, { useState, useRef, useEffect } from "react";

// ── Íconos (mismos que ya usa el proyecto) ────────────────────────────────────
const ChevronDown = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9l6 6 6-6" />
    </svg>
);
const SearchIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
    </svg>
);
const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6L6 18M6 6l12 12" />
    </svg>
);
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);
const MinusIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

/**
 * MultiSelectCheckbox
 *
 * Se usa exactamente igual que el <Select> existente, pero el dropdown
 * tiene checkboxes + buscador + seleccionar-todo.
 *
 * Props:
 *   label       — string  — etiqueta superior
 *   options     — [{ value, label }]
 *   value       — string  — valores separados por coma  ej: "RW,RF,FC"
 *   onChange    — fn(string) — recibe el nuevo string "RW,RF,FC"
 *   placeholder — string
 *   disabled    — bool
 */
export const MultiSelectCheckbox = ({
    label,
    options = [],
    value = "",
    onChange,
    placeholder = "Seleccione opciones",
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef(null);
    const searchRef = useRef(null);


    // Array de valores actualmente seleccionados
    const selected = value ? String(value).split(",").filter(Boolean) : [];

    // Cerrar al click fuera
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Enfocar buscador al abrir
    useEffect(() => {
        if (open && searchRef.current) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
    }, [open]);

    // Opciones filtradas por búsqueda
    const filtered = options.filter(opt => {
        // Convertimos ambos a string y minúsculas una sola vez por opción
        const label = String(opt.label ?? "").toLowerCase();
        const value = String(opt.value ?? "").toLowerCase();
        const searchTerm = search.toLowerCase();

        return label.includes(searchTerm) || value.includes(searchTerm);
    });

    // Toggle individual
    const toggle = (val) => {
        const stringVal = String(val); // Normalizar
        const next = selected.includes(stringVal)
            ? selected.filter(v => v !== stringVal)
            : [...selected, stringVal];
        onChange(next.join(","));
    };

    // const toggle = (val) => {
    //     const next = selected.includes(val)
    //         ? selected.filter(v => v !== val)
    //         : [...selected, val];
    //     onChange(next.join(","));
    // };

    // Seleccionar / deseleccionar todas las visibles (respeta búsqueda)
    const toggleVisible = () => {
        const visibleVals = filtered.map(o => o.value);
        const allVisible = visibleVals.every(v => selected.includes(v));
        if (allVisible) {
            // Quitar solo las visibles
            onChange(selected.filter(v => !visibleVals.includes(v)).join(","));
        } else {
            // Añadir las que faltan de las visibles
            const merged = Array.from(new Set([...selected, ...visibleVals]));
            onChange(merged.join(","));
        }
    };

    // Limpiar todo
    const clearAll = (e) => {
        e?.stopPropagation();
        onChange("");
    };

    // Quitar un tag individual desde el trigger
    const removeTag = (e, val) => {
        e.stopPropagation();
        onChange(selected.filter(v => v !== val).join(","));
    };

    // Estado del checkbox "todos"
    const visibleVals = filtered.map(o => o.value);
    const allVisible = visibleVals.length > 0 && visibleVals.every(v => selected.includes(v));
    const someVisible = visibleVals.some(v => selected.includes(v)) && !allVisible;

    // Texto del trigger
    const renderTriggerContent = () => {
        if (selected.length === 0) {
            return <span className="placeholder">{placeholder}</span>;
        }
        if (selected.length <= 2) {
            return (
                <div className="msc-tags">
                    {selected.map(val => {
                        const lbl = options.find(o => o.value === val)?.label ?? val;
                        return (
                            <span key={val} className="msc-tag">
                                {lbl}
                                <button
                                    type="button"
                                    className="msc-tag__x"
                                    onClick={(e) => removeTag(e, val)}
                                    tabIndex={-1}
                                >
                                    <XIcon />
                                </button>
                            </span>
                        );
                    })}
                </div>
            );
        }
        return (
            <div className="msc-tags">
                <span className="msc-tag msc-tag--count">
                    {selected.length} seleccionadas
                    <button
                        type="button"
                        className="msc-tag__x"
                        onClick={clearAll}
                        tabIndex={-1}
                    >
                        <XIcon />
                    </button>
                </span>
            </div>
        );
    };

    return (
        <div
            className={`pa-select msc-root ${open ? "open" : ""} ${disabled ? "disabled" : ""}`}
            ref={containerRef}
        >
            {/* Label — idéntico al pa-select normal */}
            <label className="pa-select__label">{label}</label>

            {/* Trigger — idéntico al pa-select normal */}
            <button
                type="button"
                className="pa-select__trigger msc-trigger"
                onClick={() => !disabled && setOpen(o => !o)}
                disabled={disabled}
            >
                <span className="msc-trigger__body">
                    {renderTriggerContent()}
                </span>
                <ChevronDown />
            </button>

            {/* Dropdown — aquí es donde cambia */}
            {open && !disabled && (
                <div className="pa-select__dropdown msc-dropdown">

                    {/* ── Buscador ── */}
                    <div className="msc-search">
                        <SearchIcon />
                        <input
                            ref={searchRef}
                            type="text"
                            className="msc-search__input"
                            placeholder="Buscar..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onClick={e => e.stopPropagation()}
                        />
                        {search && (
                            <button
                                type="button"
                                className="msc-search__clear"
                                onClick={() => setSearch("")}
                                tabIndex={-1}
                            >
                                <XIcon />
                            </button>
                        )}
                    </div>

                    {/* ── Seleccionar todas ── */}
                    <div className="msc-select-all" onClick={toggleVisible}>
                        <span className={`msc-cb ${allVisible ? "msc-cb--checked" : someVisible ? "msc-cb--indeterminate" : ""}`}>
                            {allVisible && <CheckIcon />}
                            {someVisible && <MinusIcon />}
                        </span>
                        <span className="msc-select-all__label">
                            {search ? `Seleccionar visibles` : "Seleccionar todas"}
                        </span>
                        <span className="msc-select-all__count">
                            {selected.length}/{options.length}
                        </span>
                    </div>

                    <div className="msc-divider" />

                    {/* ── Lista de opciones ── */}
                    <div className="msc-list">
                        {filtered.length === 0 ? (
                            <div className="msc-empty">Sin resultados para "<b>{search}</b>"</div>
                        ) : (
                            filtered.map(opt => {
                                const isChecked = selected.includes(String(opt.value));
                                return (
                                    <div
                                        key={opt.value}
                                        className={`pa-select__option msc-option ${isChecked ? "msc-option--checked" : ""}`}
                                        onClick={() => toggle(opt.value)}
                                    >
                                        <span className={`msc-cb ${isChecked ? "msc-cb--checked" : ""}`}>
                                            {isChecked && <CheckIcon />}
                                        </span>
                                        <span className="msc-option__label">{opt.label}</span>
                                        <span className="msc-option__code">{opt.value}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* ── Footer con acción limpiar / aplicar ── */}
                    {selected.length > 0 && (
                        <>
                            <div className="msc-divider" />
                            <div className="msc-footer">
                                <button type="button" className="msc-footer__clear" onClick={clearAll}>
                                    Limpiar
                                </button>
                                <button
                                    type="button"
                                    className="msc-footer__apply"
                                    onClick={() => { setOpen(false); setSearch(""); }}
                                >
                                    Aplicar · {selected.length}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiSelectCheckbox;
